<template>
  <header>
    <nav class="nav">
      <div class="flex-item">
        <div class="logo">
          <router-link to="/">
            <img :src="logoSrc" alt="Gildi logo" class="logo-img" />
          </router-link>
        </div>

        <ul class="nav-links">
          <li
            v-for="link in navLinks"
            :key="link.to"
            :class="{ active: isActive(link.to) }"
          >
            <template v-if="link.children?.length">
              <v-menu open location="bottom" offset-y>
                <template #activator="{ props }">
                  <span v-bind="props" class="nav-item-button">
                    {{ link.label }}
                  </span>
                </template>
                <v-list class="nav-dropdown">
                  <v-list-item
                    v-for="child in link.children"
                    :key="child.to"
                    :to="child.to"
                    link
                    class="nav-dropdown-item"
                    :title="child.label"
                  />
                </v-list>
              </v-menu>
            </template>
            <template v-else>
              <router-link :to="link.to" class="nav-item-link">
                {{ link.label }}
              </router-link>
            </template>
          </li>
        </ul>
      </div>

      <div class="actions">
        <!-- Dark mode toggle via useApplyDarkMode -->
        <DarkModeToggle class="dark-mode-toggle"/>

        <!-- Language menu -->
        <v-menu v-model="isLangOpen" location="bottom end" offset-y>
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="text"
              class="icon-button"
              aria-label="Language"
            >
              <v-icon size="20" icon="mdi-web" />
            </v-btn>
          </template>

          <v-list class="lang-menu">
            <v-list-item class="lang-item" @click="changeLang('isl')">
              <div class="lang-row">
                <img :src="IcelandicFlag" alt="Íslenska" class="flag" />
                <span>Íslenska</span>
              </div>
            </v-list-item>
            <v-list-item class="lang-item" @click="changeLang('en')">
              <div class="lang-row">
                <img :src="ENFlag" alt="English" class="flag" />
                <span>English</span>
              </div>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Profile menu -->
        <v-menu v-model="isMenuOpen" location="bottom end" offset-y>
          <template #activator="{ props }">
            <button v-bind="props" class="profile-button">
              <span class="name">{{ authUser?.name }}</span>
              <v-icon
                class="chevron"
                :class="{ open: isMenuOpen }"
                icon="mdi-chevron-down"
                size="18"
              />
            </button>
          </template>
          <v-list>
            <v-list-item :to="'/stillingar'" link :title="t('stillingar_header')" />
            <v-list-item :title="t('header_utskraning')" @click="logout" />
          </v-list>
        </v-menu>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/useAuthStore'
import IcelandicFlag from '@/assets/icons/icelandic_flag.svg'
import ENFlag from '@/assets/icons/EN.svg'
import { useAssetPath } from '@/composables/useAssetPath'
import { useApplyDarkMode } from '@/utils/theme'
import { useTheme } from 'vuetify'
import DarkModeToggle from '@/components/shared/DarkModeToggle.vue'

const logoSrc = useAssetPath('gildi.svg')

const router = useRouter()
const route = useRoute()
const { locale, t } = useI18n()
const authStore = useAuthStore()
const authUser = computed(() => authStore.authUser)

const isMenuOpen = ref(false)
const isLangOpen = ref(false)
const isDarkMode = ref(false)

// dark-mode helper from your util
const applyDarkMode = useApplyDarkMode()
const theme = useTheme()

function logout() {
  authStore.logOut()
}

function changeLang(lang: string) {
  locale.value = lang
  isLangOpen.value = false
}

function isActive(path: string) {
  return route.path === path || route.path.startsWith(path + '/')
}

const navLinks = computed(() => {
  const seen = new Set<string>()
  return router
    .getRoutes()
    .filter(r => {
      if (r.path === '/' || r.path === '/stillingar' || r.path === '/impersonate') return false
      const isTopLevel = r.path.split('/').filter(Boolean).length === 1
      return r.meta?.breadcrumb && isTopLevel
    })
    .filter(r => {
      const key = String(r.meta?.breadcrumb)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .map(r => {
      const children =
        r.children
          ?.filter(c => c.meta?.breadcrumb)
          .map(c => ({
            to: r.path.replace(/\/$/, '') + '/' + c.path,
            label: t(String(c.meta?.breadcrumb || c.meta?.titleKey || '')),
          })) || []
      return {
        to: children.length ? children[0].to : r.path,
        label: t(String(r.meta?.breadcrumb || r.meta?.titleKey || '')),
        children,
      }
    })
})

function toggleDarkMode() {
  isDarkMode.value = !isDarkMode.value
  applyDarkMode(isDarkMode.value) // updates body class + Vuetify theme
}

onMounted(() => {
  // Initialize from current Vuetify theme
  isDarkMode.value = !!theme.global.current.value.dark
  applyDarkMode(isDarkMode.value) // ensure body class is in sync
})
</script>

<style scoped lang="scss">
@use "@/scss/settings/variables" as var;

.nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var.$color-background;
  padding: var.$spacing-xl 0;
  flex-wrap: nowrap;
  max-width: 100%;
  overflow: hidden;

  .flex-item {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    flex: 1;
    min-width: 0;
  }

  .logo-img {
    height: 40px;
    width: auto;
    padding-right: var.$spacing-xl;
  }

  .nav-links {
    display: flex;
    list-style: none;
    gap: var.$gap-lg;
    margin-left: var.$spacing-lg;
    margin-top: var.$spacing-sm;

    li {
      padding: 0 8px 4px 8px;
      border-bottom: 1.5px solid transparent;
      display: flex;
      align-items: center;

      &.active {
        border-color: var.$color-primary;

        .nav-item-link,
        .nav-item-button {
          color: var.$color-primary;
        }
      }

      .nav-item-link,
      .nav-item-button {
        font-weight: var.$font-weight-bold;
        text-decoration: none;
        color: var.$color-text;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;

        &:hover {
          color: var.$color-primary;
        }
      }
    }
  }

  .actions {
    display: flex;
    align-items: center;
    gap: var.$spacing-md;

    .icon-button {
      padding: 0;
      display: flex;
      color: var(--v-theme-on-surface);
      transition: color .2s ease;

      &:hover {
        color: var(--v-theme-primary);
      }
    }
  }

  .profile-button {
    background: none;
    border: none;
    cursor: pointer;
    font-weight: var.$font-weight-medium;
    color: var.$color-text;
    display: flex;
    align-items: center;
    gap: 6px;

    .name {
      white-space: nowrap;
    }

    .chevron {
      transition: transform 0.2s ease;
    }

    .chevron.open {
      transform: rotate(180deg);
    }
  }
}

.lang-menu {
  .lang-item {
    padding: 8px 16px;
    cursor: pointer;

    &:hover {
      background: var.$color-surface;
    }

    .lang-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .flag {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    span {
      font-size: var.$font-size-base;
      color: var.$color-text;
      line-height: 1;
    }
  }
}

.v-list-item {
  min-width: 160px;
}

/* Dark mode context */
body.dark-mode .nav {
  background-color: var.$color-background-dark;
}

body.dark-mode .profile-button {
  color: var.$color-text-dark;

  &:hover {
    color: var.$color-primary-dark;
  }
}

body.dark-mode .nav-links li a {
  color: var.$color-text-dark;

  &:hover {
    color: var.$color-primary-dark;
  }
}

/* Menu styling */
.nav-dropdown {
  padding: 8px 0;
  min-width: 160px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08) !important;
  border-radius: 6px !important;
  background-color: var(--v-theme-surface);
  left: -8px;
}

.nav-dropdown-item {
  font-weight: 500 !important;
  font-size: var(--v-font-size-base, 14px);
  padding: 10px 16px !important;
  color: var(--v-theme-on-surface) !important;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03) !important;
  }
}

/* Toggle button */
.dark-mode-toggle {
  color: var(--v-theme-on-surface);
  transition: color .2s ease;

  &:hover {
    color: var(--v-theme-primary);
  }
}
</style>
